// src/services/axiosService.js
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import axiosFetch from 'configs/fetchInterceptor';
import appService from './appService';
import { onProduction, throwError } from 'utils/helpers';
import i18n from 'i18next';
import { buildRequestLocaleHeaders } from 'utils/localeUtils';
import { interceptApiCall } from 'services/apiTracker';

// *******************************************************************************
// INTERNAL STATE
// *******************************************************************************
const inFlightRequests = new Map(); // <requestKey, Promise>

// *******************************************************************************
// DEDUPLICATION HELPERS
// *******************************************************************************
/** Generates a unique key for request deduplication */
const generateRequestKey = (method, url, data = null, headers = {}) => {
  const parts = [method.toUpperCase(), url];

  // Include data hash for POST/PUT/PATCH requests
  if (data && ['POST', 'PUT', 'PATCH'].includes(method.toUpperCase())) {
    try {
      parts.push(JSON.stringify(data));
    } catch (e) {
      parts.push(String(data));
    }
  }

  return parts.join('::');
};

/** Wraps a request with deduplication logic (only in development) */
const dedupeRequest = (requestKey, requestFn) => {
  // In production, skip dedup to avoid masking race conditions
  if (onProduction()) {
    return requestFn();
  }

  // Dev/StrictMode: dedupe using a shared Promise stored immediately
  if (inFlightRequests.has(requestKey)) {
    return inFlightRequests.get(requestKey);
  }

  const requestPromise = requestFn().finally(() => {
    inFlightRequests.delete(requestKey);
  });

  inFlightRequests.set(requestKey, requestPromise);
  return requestPromise;
};

// *******************************************************************************
// HELPERS
// *******************************************************************************
/** Validates if URL is valid before making request */
const validateUrl = (url, method = 'REQUEST') => {
  if (!url || typeof url !== 'string') {
    // 🚨 CRITICAL ERROR: Show VERY visible error in console
    console.error('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    console.error(
      '%c🚨 CRITICAL ERROR: INVALID URL IN AXIOSSERVICE',
      'color: #ff0000; font-weight: bold; font-size: 16px; background: #ffebee; padding: 8px;'
    );
    console.error('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    console.error('%cHTTP Method:', 'font-weight: bold;', method.toUpperCase());
    console.error('%cReceived URL:', 'font-weight: bold;', url);
    console.error('%cURL Type:', 'font-weight: bold;', typeof url);
    console.error(
      '%c⚠️ WARNING:',
      'font-weight: bold; color: #ff6b6b;',
      'You probably tried to access a property that does not exist in API/constants'
    );
    console.error(
      '%cExample:',
      'font-weight: bold;',
      'API.ACCOUNTS?.SEND_EMAIL (where ACCOUNTS or SEND_EMAIL do not exist)'
    );
    console.error('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    console.error('%cStack Trace (where error occurred):', 'font-weight: bold; color: #ff6b6b;');
    console.trace();
    console.error('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');

    const errorMsg = `[axiosService] Invalid or undefined URL in method ${method.toUpperCase()}`;
    appService.console('e', errorMsg, { url, method });

    // Throw exception to prevent execution
    throw new Error(errorMsg);
  }
};

const getDefaultHeaders = () => axiosFetch?.defaults?.headers || {};

// injects locale + allows per-request override
const mergeHeaders = (customHeaders = {}) => ({
  ...getDefaultHeaders(),
  ...buildRequestLocaleHeaders(i18n), // accept-language, x-language, x-countrycode, x-locale
  ...customHeaders,
});

const validateOptions = (options, requiredFields = []) => {
  requiredFields.forEach(field => {
    if (!options?.[field]) {
      appService.console('e', `'${field}' is missing in axiosService`);
    }
  });
};

const done = (result, returnAsObject) => {
  if (!result?.data) return [null, result];

  const { data } = result;
  const docs = returnAsObject
    ? Array.isArray(data)
      ? data[0] || null
      : data
    : Array.isArray(data)
      ? data.length > 0
        ? data
        : null
      : [data];

  return [docs, result];
};

const buildUrlWithQuery = (url, qs = {}) => {
  const queryString = Object.entries(qs || {})
    .flatMap(([key, value]) =>
      Array.isArray(value)
        ? value.map(item => `${encodeURIComponent(key)}=${encodeURIComponent(item)}`)
        : `${encodeURIComponent(key)}=${encodeURIComponent(value)}`
    )
    .join('&');

  return queryString ? `${url}?${queryString}` : url;
};

const resultadoGetNovo = {
  success: true,
  error: false,
  records: 1,
  data: {
    author: 'Sigmund Freud',
    quote:
      'A maioria das pessoas nao quer realmente a liberdade, pois liberdade envolve responsabilidade, e a maioria das pessoas tem medo de responsabilidade.',
  },
  summary: {
    startServerTime: 1762348658309,
    finishServerTime: 1762348658313,
    frequency: { externalRequests: 0, internalRequests: 0, databaseProcess: 0 },
    elapsedTime: {
      network: 7,
      server: 4,
      internalRequests: 0,
      externalRequests: 0,
      databaseProcess: 0,
      full: 11,
    },
    tokenInfo: null,
  },
};

const resultadoGetAntigo = {
  success: true,
  error: false,
  records: 1,
  data: {
    author: 'Leonardo da Vinci',
    quote: 'Para estar junto nao e preciso estar perto, e sim do lado de dentro.',
  },
  summary: {
    startServerTime: 1762348719629,
    finishServerTime: 1762348719630,
    frequency: { externalRequests: 0, internalRequests: 0, databaseProcess: 0 },
    elapsedTime: {
      network: 4,
      server: 1,
      internalRequests: 0,
      externalRequests: 0,
      databaseProcess: 0,
      full: 5,
    },
    tokenInfo: null,
  },
};

// *******************************************************************************
// MAIN SERVICE
// *******************************************************************************
const axiosService = {
  async get({ url, headers = {}, returnAsObject = false, qs }) {
    if (qs) url = buildUrlWithQuery(url, qs);
    // Validate URL before anything else
    validateUrl(url, 'GET');

    // API Tracker: Intercept before request (throws if aborted)
    interceptApiCall(url, 'get', {}, headers);

    const requestKey = generateRequestKey('GET', url, null, headers);

    return dedupeRequest(requestKey, async () => {
      const payload = {
        url,
        method: 'get',
        headers: mergeHeaders({ ...headers, 'x-resultasobject': returnAsObject }),
      };

      try {
        const result = await axiosFetch(payload);
        return done(result, returnAsObject);
      } catch (error) {
        // Re-throw API Tracker aborts and other errors
        throw error;
      }
    });
  },

  async post({ url, headers = {}, data = {}, allValues, returnAsObject = true, timeout = null }) {
    // Validate URL before anything else
    validateUrl(url, 'POST');

    // Check if data is FormData (file upload) - skip decorator and deduplication
    const isFormData = data instanceof FormData;

    // ─────────────────────────────────────────────────────────────────────────
    // EXECUTE REQUEST DECORATOR (before apiTracker to show RAW and DECORATED)
    // Skip for FormData since it cannot be JSON serialized
    // ─────────────────────────────────────────────────────────────────────────
    let dataRaw = null;
    let dataDecorated = data;
    let decoratorExecuted = false;

    if (!isFormData) {
      try {
        const useZustand = (await import('hooks/useZustand')).default;
        const currentPage = useZustand('module', 'currentPage', false);
        const decoratorFn = currentPage?.decorators?.request;

        if (typeof decoratorFn === 'function' && data) {
          // Save RAW data BEFORE transformation (deep clone to avoid reference issues)
          dataRaw = JSON.parse(JSON.stringify(data));

          const ctx = {
            data,
            allValues,
            url,
            method: 'post',
          };

          dataDecorated = decoratorFn(ctx);
          decoratorExecuted = true;
        }
      } catch (decoratorError) {
        console.warn('⚠️ Request decorator failed:', decoratorError);
      }

      // API Tracker: Intercept before request (throws if aborted)
      // Pass decorated data and metadata to apiTracker
      interceptApiCall(
        url,
        'post',
        dataDecorated,
        headers,
        decoratorExecuted ? { dataRaw, dataDecorated } : null
      );
    }

    // For FormData, skip deduplication (file uploads should not be deduplicated)
    const executeRequest = async () => {
      // For FormData, don't override Content-Type - let browser set it with boundary
      const finalHeaders = isFormData
        ? mergeHeaders({ ...headers, 'x-resultasobject': returnAsObject })
        : mergeHeaders({ ...headers, 'x-resultasobject': returnAsObject });

      // Remove Content-Type for FormData to let browser set it with boundary
      if (isFormData && finalHeaders['Content-Type']) {
        delete finalHeaders['Content-Type'];
      }

      const payload = {
        url,
        method: 'post',
        headers: finalHeaders,
        data: dataDecorated,
        _decoratorExecuted: decoratorExecuted,
        _dataRaw: dataRaw,
        _dataDecorated: dataDecorated,
        ...(timeout && { timeout }), // Override default timeout if provided
      };

      try {
        const result = await axiosFetch(payload);
        return done(result, returnAsObject);
      } catch (error) {
        throw error;
      }
    };

    // Skip deduplication for FormData uploads
    if (isFormData) {
      return executeRequest();
    }

    const requestKey = generateRequestKey('POST', url, dataDecorated, headers);
    return dedupeRequest(requestKey, executeRequest);
  },

  async put({ url, headers = {}, data = {}, allValues, returnAsObject = true }) {
    // Validate URL before anything else
    validateUrl(url, 'PUT');

    // ─────────────────────────────────────────────────────────────────────────
    // EXECUTE REQUEST DECORATOR (before apiTracker to show RAW and DECORATED)
    // ─────────────────────────────────────────────────────────────────────────
    let dataRaw = null;
    let dataDecorated = data;
    let decoratorExecuted = false;

    try {
      const useZustand = (await import('hooks/useZustand')).default;
      const currentPage = useZustand('module', 'currentPage', false);
      const decoratorFn = currentPage?.decorators?.request;

      if (typeof decoratorFn === 'function' && data) {
        // Save RAW data BEFORE transformation (deep clone to avoid reference issues)
        dataRaw = JSON.parse(JSON.stringify(data));

        const ctx = {
          data,
          allValues,
          url,
          method: 'put',
        };

        dataDecorated = decoratorFn(ctx);
        decoratorExecuted = true;
      }
    } catch (decoratorError) {
      console.warn('⚠️ Request decorator failed:', decoratorError);
    }

    // API Tracker: Intercept before request (throws if aborted)
    // Pass decorated data and metadata to apiTracker
    interceptApiCall(
      url,
      'put',
      dataDecorated,
      headers,
      decoratorExecuted ? { dataRaw, dataDecorated } : null
    );

    const requestKey = generateRequestKey('PUT', url, dataDecorated, headers);

    return dedupeRequest(requestKey, async () => {
      const payload = {
        url,
        method: 'put',
        headers: mergeHeaders({ ...headers, 'x-resultasobject': returnAsObject }),
        data: dataDecorated,
        _decoratorExecuted: decoratorExecuted,
        _dataRaw: dataRaw,
        _dataDecorated: dataDecorated,
      };

      try {
        const result = await axiosFetch(payload);
        return done(result, returnAsObject);
      } catch (error) {
        throw error;
      }
    });
  },

  async patch({ url, headers = {}, data = {}, allValues, returnAsObject = true }) {
    // Validate URL before anything else
    validateUrl(url, 'PATCH');

    // ─────────────────────────────────────────────────────────────────────────
    // EXECUTE REQUEST DECORATOR (before apiTracker to show RAW and DECORATED)
    // ─────────────────────────────────────────────────────────────────────────
    let dataRaw = null;
    let dataDecorated = data;
    let decoratorExecuted = false;

    try {
      const useZustand = (await import('hooks/useZustand')).default;
      const currentPage = useZustand('module', 'currentPage', false);
      const decoratorFn = currentPage?.decorators?.request;

      if (typeof decoratorFn === 'function' && data) {
        // Save RAW data BEFORE transformation (deep clone to avoid reference issues)
        dataRaw = JSON.parse(JSON.stringify(data));

        const ctx = {
          data,
          allValues,
          url,
          method: 'patch',
        };

        dataDecorated = decoratorFn(ctx);
        decoratorExecuted = true;
      }
    } catch (decoratorError) {
      console.warn('⚠️ Request decorator failed:', decoratorError);
    }

    // API Tracker: Intercept before request (throws if aborted)
    // Pass decorated data and metadata to apiTracker
    interceptApiCall(
      url,
      'patch',
      dataDecorated,
      headers,
      decoratorExecuted ? { dataRaw, dataDecorated } : null
    );

    const requestKey = generateRequestKey('PATCH', url, dataDecorated, headers);

    return dedupeRequest(requestKey, async () => {
      const payload = {
        url,
        method: 'patch',
        headers: mergeHeaders({ ...headers, 'x-resultasobject': returnAsObject }),
        data: dataDecorated,
        _decoratorExecuted: decoratorExecuted,
        _dataRaw: dataRaw,
        _dataDecorated: dataDecorated,
      };

      try {
        const result = await axiosFetch(payload);
        return done(result, returnAsObject);
      } catch (error) {
        throw error;
      }
    });
  },

  async delete({ url, headers = {}, data = {}, returnAsObject = true }) {
    // Validate URL before anything else
    validateUrl(url, 'DELETE');

    // API Tracker: Intercept before request (throws if aborted)
    interceptApiCall(url, 'delete', data, headers);

    const requestKey = generateRequestKey('DELETE', url, data, headers);

    return dedupeRequest(requestKey, async () => {
      const payload = {
        url,
        method: 'delete',
        headers: mergeHeaders({ ...headers, 'x-resultasobject': returnAsObject }),
      };

      try {
        const result = await axiosFetch(payload);
        return done(result, returnAsObject);
      } catch (error) {
        throw error;
      }
    });
  },

  async upsert({ data = {}, ...rest }) {
    const _id = data?._id;

    if (_id) {
      delete data?._id;
      return await this.put({ ...rest, data });
    } else {
      return await this.post({ ...rest, data });
    }
  },

  async assertFieldNotExists(url, field, value) {
    try {
      const encodedField = encodeURIComponent(field);
      const encodedValue = encodeURIComponent(value);

      const [doc, result] = await this.get({
        url: `${url}/get-by-field/${encodedField}/${encodedValue}`,
      });
      return [doc, result];
    } catch (error) {
      throwError('assertFieldNotExists Error', error);
    }
  },
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default axiosService;
